import React, { useContext, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { ProductContext } from "../context/AppContext";
import Header from "./Header";
import { getCachedData, setCachedData } from "../utils/apiCache";
import {
    FaCreditCard, FaClock, FaReceipt, FaCheckCircle,
    FaTimesCircle, FaHistory, FaArrowLeft, FaUtensils,
    FaCalendarAlt
} from "react-icons/fa";
import Swal from "sweetalert2";

const CanteenHistory = () => {
    const [orderHistory, setOrderHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all');
    const ORDERS_PER_PAGE = 6;
    const [visibleOrderCount, setVisibleOrderCount] = useState(ORDERS_PER_PAGE);
    const navigate = useNavigate();
    const [studentId, setStudentId] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [studentProfile, setStudentProfile] = useState({});
    const { userId, setClassSectionId, Automationdata } = useContext(ProductContext);
    const authtoken = localStorage.getItem("token");

    // Filter options
    const filters = [
        { key: 'all', label: 'All Orders', icon: <FaHistory /> },
        { key: 'paid', label: 'Paid', icon: <FaCheckCircle /> },
        { key: 'unpaid', label: 'Unpaid', icon: <FaClock /> }
    ];

    useEffect(() => {
        if (!userId) return;

        const fetchStudentData = async () => {
            const cacheKey = `canteen_history_stud_${userId}`;
            const cached = getCachedData(cacheKey);
            if (cached) {
                setStudentId(cached.id);
                setSessionId(cached.session);
                setClassSectionId(cached.class_section_id);
                setStudentProfile(cached);
                
            }
            try {
                const response = await fetch(`https://api.mirai.healthcare/api/student?regs_id=${userId}`, {
                    headers: { "x-auth": `${authtoken}` }
                });
                const data = await response.json();
                if (data.success && data.message.length > 0) {
                    const studentData = data.message[0];
                    setStudentId(studentData.id);
                    setSessionId(studentData.session);
                    setClassSectionId(studentData.class_section_id);
                    setStudentProfile(studentData);
                    setCachedData(cacheKey, studentData);
                }
            } catch (error) {
                console.error("Error fetching student data:", error);
            }
        };
        fetchStudentData();
    }, [userId, authtoken]);

    useEffect(() => {
        if (!studentId) return;

        const fetchOrderHistory = async () => {
            if (!studentId) return;
            const cacheKey = `canteen_orders_${studentId}`;
            const cached = getCachedData(cacheKey);
            if (cached) {
                setOrderHistory(cached);
                setLoading(false);
                
            }
            try {
                if (!cached) setLoading(true);
                const response = await axios.get(
                    `https://api.mirai.healthcare/api/student-details/canteen-order?user_type=student&student_id=${studentId}`,
                    {
                        headers: { "x-auth": authtoken },
                    }
                );
                const sortedOrders = (response.data.message || []).sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                );
                setOrderHistory(sortedOrders);
                setCachedData(cacheKey, sortedOrders);
            } catch (error) {
                console.error("Error fetching order history:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchOrderHistory();
    }, [studentId, authtoken]);

    const handlePayNow = async (order) => {
        try {
            const studentFullName = studentProfile.student_full_name || 'Student';
            const allMobiles = [
                studentProfile.contact_phone,
            ].filter(Boolean).join(", ");
            const allEmails = [
                studentProfile.contact_email,
            ].filter(Boolean).join(", ");

            const paymentDetails = {
                amount: order.amt,
                productinfo: 'Canteen Order',
                firstname: studentFullName,
                email: allEmails,
                phone: allMobiles,
                canteenId: order.id,
                paymentMethod: Automationdata?.data?.[0]?.PaymentMethod,
            };

            const query = new URLSearchParams({
                ...paymentDetails,
                authToken: authtoken || '',
            }).toString();

            const url = `${window.location.origin}/pay-u-payment?${query}`;
            window.open(url, '_blank');

            Swal.fire({
                title: "Payment Started",
                text: "Redirecting to secure payment gateway...",
                icon: "info",
                timer: 2000,
                showConfirmButton: false,
                toast: true,
                position: "top-end"
            });
        } catch (error) {
            console.error("Payment initiation error:", error);
            Swal.fire({
                title: "Payment Error",
                text: "Couldn't start payment. Please try again.",
                icon: "error",
                confirmButtonColor: "#dc3545"
            });
        }
    };

    const filteredOrders = orderHistory.filter(order => {
        if (activeFilter === 'all') return true;
        return order.pay_status?.toLowerCase() === activeFilter;
    });

    // Long histories stay light on mobile - show a page at a time
    const visibleOrders = filteredOrders.slice(0, visibleOrderCount);
    const remainingOrders = filteredOrders.length - visibleOrders.length;

    useEffect(() => {
        setVisibleOrderCount(ORDERS_PER_PAGE);
    }, [activeFilter, orderHistory]);

    const getStats = () => ({
    total: orderHistory.length,
    paid: orderHistory.filter(o => o.pay_status?.toLowerCase() === 'paid').length,
    unpaid: orderHistory.filter(o => o.pay_status?.toLowerCase() === 'unpaid').length,
    totalSpent: orderHistory.reduce((sum, o) => sum + parseFloat(o.amt || 0), 0).toFixed(2)
});


    const stats = getStats();

    if (loading) {
        return (
            <div className="page-wrapper min-vh-100 ordh-page">
                <Header />
                <div className="ordh-state">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <p>Loading your orders...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper min-vh-100 ordh-page">
            <Header />

            <div className="ordh-wrap">

                {/* Header */}
                <div className="ordh-head">
                    <Link to="/canteen" className="ordh-back" title="Back to menu">
                        <FaArrowLeft />
                    </Link>
                    <div className="ordh-head__text">
                        <h5>Order History</h5>
                        <span>All your canteen orders in one place</span>
                    </div>
                    <Link to="/canteen" className="ordh-menu-btn">
                        <FaUtensils />
                        Menu
                    </Link>
                </div>

                {/* Summary strip */}
                <div className="ordh-stats">
                    <div className="ordh-stat">
                        <span className="ordh-stat__label">Orders</span>
                        <strong className="ordh-stat__value">{stats.total}</strong>
                    </div>
                    <div className="ordh-stat">
                        <span className="ordh-stat__label">Paid</span>
                        <strong className="ordh-stat__value is-paid">{stats.paid}</strong>
                    </div>
                    <div className="ordh-stat">
                        <span className="ordh-stat__label">Pending</span>
                        <strong className="ordh-stat__value is-pending">{stats.unpaid}</strong>
                    </div>
                    <div className="ordh-stat">
                        <span className="ordh-stat__label">Spent</span>
                        <strong className="ordh-stat__value is-total">₹{stats.totalSpent}</strong>
                    </div>
                </div>

                {/* Filter chips */}
                <div className="ordh-chips">
                    {filters.map((filter) => (
                        <button
                            key={filter.key}
                            type="button"
                            className={`ordh-chip ${activeFilter === filter.key ? "is-active" : ""}`}
                            onClick={() => setActiveFilter(filter.key)}
                        >
                            {filter.icon}
                            {filter.label}
                        </button>
                    ))}
                </div>

                {/* Orders */}
                {filteredOrders.length > 0 ? (
                    <>
                    <div className="ordh-list">
                        {visibleOrders.map((order, index) => {
                            const isUnpaid = order.pay_status?.toLowerCase() === "unpaid";
                            const isPaid = order.pay_status?.toLowerCase() === "paid";
                            const lines = order.ord_line || [];

                            return (
                                <div className="ordh-row" key={order.id || index}>

                                    {/* Timeline rail */}
                                    <div className="ordh-rail">
                                        <span
                                            className="ordh-node"
                                            data-state={isPaid ? "paid" : isUnpaid ? "pending" : "other"}
                                        >
                                            <FaReceipt />
                                        </span>
                                    </div>

                                    <div className="ordh-card">

                                    {/* Card head */}
                                    <div className="ordh-card__head">
                                        <div className="ordh-card__id">
                                            <div>
                                                <strong>Order #{order.order_id || order.id}</strong>
                                                <span>
                                                    {new Date(order.created_at).toLocaleDateString("en-IN", {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                    })}
                                                </span>
                                            </div>
                                        </div>
                                        <span className={`ordh-pill ${isPaid ? "is-paid" : isUnpaid ? "is-pending" : "is-other"}`}>
                                            {isPaid ? <FaCheckCircle /> : isUnpaid ? <FaClock /> : <FaTimesCircle />}
                                            {isPaid ? "Paid" : isUnpaid ? "Pending" : order.pay_status}
                                        </span>
                                    </div>

                                    {/* Delivery date */}
                                    {order.order_for && (
                                        <div className="ordh-for">
                                            <FaCalendarAlt />
                                            For{" "}
                                            <strong>
                                                {new Date(order.order_for).toLocaleDateString("en-IN", {
                                                    weekday: "short",
                                                    day: "2-digit",
                                                    month: "short",
                                                })}
                                            </strong>
                                            {order.status && (
                                                <span className="ordh-status">{order.status}</span>
                                            )}
                                        </div>
                                    )}

                                    {/* Items */}
                                    <div className="ordh-items">
                                        {lines.length > 0 ? (
                                            lines.map((item, idx) => (
                                                <div className="ordh-line" key={idx}>
                                                    <span className="ordh-line__qty">{item.qty}×</span>
                                                    <span className="ordh-line__name" title={item.rmk}>
                                                        {item.rmk || `Item ${idx + 1}`}
                                                    </span>
                                                    <span className="ordh-line__amt">
                                                        ₹{parseFloat(item.amt).toFixed(2)}
                                                    </span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="ordh-line ordh-line--empty">No items listed</div>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    <div className="ordh-card__foot">
                                        <div className="ordh-total">
                                            <span>Total</span>
                                            <strong>₹{parseFloat(order.amt).toFixed(2)}</strong>
                                        </div>

                                        {isUnpaid ? (
                                            <button
                                                type="button"
                                                className="ordh-pay"
                                                onClick={() => handlePayNow(order)}
                                            >
                                                <FaCreditCard />
                                                Pay ₹{order.amt}
                                            </button>
                                        ) : isPaid ? (
                                            <span className="ordh-done">
                                                <FaCheckCircle />
                                                Payment complete
                                            </span>
                                        ) : null}
                                    </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                        {remainingOrders > 0 && (
                            <button
                                type="button"
                                className="ordh-more"
                                onClick={() => setVisibleOrderCount((prev) => prev + ORDERS_PER_PAGE)}
                            >
                                Load More
                                <span>({remainingOrders} left)</span>
                            </button>
                        )}
                    </>
                ) : (
                    <div className="ordh-state">
                        <FaHistory className="ordh-state__icon" />
                        <p className="ordh-state__title">No orders yet</p>
                        <p>
                            {activeFilter === "all"
                                ? "Place your first canteen order to see it here."
                                : "No orders match this filter."}
                        </p>
                        <Link to="/canteen" className="ordh-state__btn">
                            <FaUtensils /> Browse menu
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CanteenHistory;